import {
  ApplicationRef,
  Injectable,
  PLATFORM_ID,
  effect,
  inject,
  signal,
  computed,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  SwUpdate,
  VersionReadyEvent,
  VersionInstallationFailedEvent,
} from '@angular/service-worker';
import {
  EMPTY,
  delay,
  filter,
  first,
  from,
  timer,
  switchMap,
  tap,
  catchError,
  of,
  timeout,
  Subscription,
} from 'rxjs';

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'activating'
  | 'failed'
  | 'unrecoverable'
  | 'resetting'; // ← nuevo estado para el nuclear reset

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SW_READY_DELAY_MS = 3000;

// ─── Configuración del nuclear reset ─────────────────────────────────────────
// Después de MAX_INSTALL_FAILURES intentos fallidos consecutivos el SW se
// desregistra completamente, se limpian todas las cachés y se recarga la página.
// El flag SW_RESET_KEY en sessionStorage evita que el reset se dispare en loop:
// si la página recién recargó tras un reset y el SW sigue fallando, no vuelve
// a resetear en la misma sesión — el usuario verá el estado 'failed' y podrá
// recargar manualmente.
const MAX_INSTALL_FAILURES = 3;
const SW_RESET_KEY = 'sw_nuclear_reset_at';
const SW_RESET_COOLDOWN_MS = 60 * 60 * 1000; // 1 hora entre resets automáticos

@Injectable({ providedIn: 'root' })
export class UpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly enabled = this.isBrowser && this.swUpdate.isEnabled;

  // ─── Estado central ───────────────────────────────────────────────────────
  readonly status = signal<UpdateStatus>('idle');
  readonly updateAvailable = computed(() => this.status() === 'available');
  readonly isActivating = computed(() => this.status() === 'activating');
  readonly isResetting = computed(() => this.status() === 'resetting');

  // ─── Control de reintentos ────────────────────────────────────────────────
  private installFailureCount = 0;
  private retrySubscription: Subscription | null = null;

  // ─── Logging ──────────────────────────────────────────────────────────────
  private log(
    level: 'info' | 'warn' | 'error',
    message: string,
    data?: unknown
  ): void {
    const prefix = `[SW:${new Date().toISOString()}]`;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    data
      ? console[level](`${prefix} ${message}`, data)
      : console[level](`${prefix} ${message}`);
  }

  // ─── Signals derivados de Observables ────────────────────────────────────
  private readonly versionReady = toSignal(
    this.enabled
      ? this.swUpdate.versionUpdates.pipe(
          filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY')
        )
      : EMPTY,
    { initialValue: null }
  );

  private readonly installFailed = toSignal(
    this.enabled
      ? this.swUpdate.versionUpdates.pipe(
          filter(
            (e): e is VersionInstallationFailedEvent =>
              e.type === 'VERSION_INSTALLATION_FAILED'
          )
        )
      : EMPTY,
    { initialValue: null }
  );

  private readonly unrecoverable = toSignal(
    this.enabled ? this.swUpdate.unrecoverable : EMPTY,
    { initialValue: null }
  );

  // ─── Verificación periódica ───────────────────────────────────────────────
  private readonly periodicCheck = toSignal(
    this.enabled
      ? this.appRef.isStable.pipe(
          first(stable => stable),
          delay(SW_READY_DELAY_MS),
          switchMap(() => {
            this.log(
              'info',
              `✅ App stable. Iniciando checks cada ${CHECK_INTERVAL_MS / 60000} min`
            );
            return timer(0, CHECK_INTERVAL_MS);
          }),
          tap(() => {
            this.log('info', '🔍 Verificando actualizaciones...');
            this.status.set('checking');
          }),
          switchMap(() =>
            from(this.swUpdate.checkForUpdate()).pipe(
              timeout(10_000),
              tap(updateFound => {
                if (updateFound) {
                  this.log('warn', '📦 Nueva versión disponible');
                } else {
                  this.log('info', '✓ App al día');
                  this.status.set('idle');
                }
              }),
              catchError(err => {
                const isTimeout = err?.name === 'TimeoutError';
                this.log(
                  'error',
                  isTimeout ? '⏱️ Check timeout (10s)' : '❌ Check fallido',
                  err
                );
                this.status.set('idle');
                return of(null);
              })
            )
          )
        )
      : EMPTY,
    { initialValue: null }
  );

  // ─── Effects ──────────────────────────────────────────────────────────────
  constructor() {
    if (this.isBrowser) {
      this.log(
        'info',
        `🚀 UpdateService ${this.enabled ? '✅ ENABLED' : '⚠️ DISABLED'}`
      );
      this.logResetHistory();
    }

    // Effect 1: VERSION_READY → resetear contador y notificar
    effect(() => {
      if (!this.versionReady()) return;
      this.installFailureCount = 0;
      this.retrySubscription?.unsubscribe();
      this.log('info', '✅ VERSION_READY: Nueva versión lista');
      this.status.set('available');
      this.notifyUser('available');
    });

    // Effect 2: VERSION_INSTALLATION_FAILED → retry con límite → nuclear reset
    effect(() => {
      const event = this.installFailed();
      if (!event) return;

      // Cancelar retry anterior pendiente
      this.retrySubscription?.unsubscribe();
      this.installFailureCount++;

      this.log(
        'error',
        `❌ VERSION_INSTALLATION_FAILED (intento ${this.installFailureCount}/${MAX_INSTALL_FAILURES}): ${event.error}`,
        event.error
      );

      this.status.set('failed');
      this.notifyUser('failed');

      if (this.installFailureCount >= MAX_INSTALL_FAILURES) {
        // ── Nuclear reset ──────────────────────────────────────────────────
        this.log(
          'warn',
          `🚨 ${MAX_INSTALL_FAILURES} fallos consecutivos. Evaluando nuclear reset...`
        );

        if (this.canPerformReset()) {
          this.log('warn', '💣 Iniciando nuclear reset en 5 segundos...');
          this.status.set('resetting');
          this.notifyUser('resetting');
          timer(5000)
            .pipe(switchMap(() => from(this.performNuclearReset())))
            .subscribe();
        } else {
          this.log(
            'warn',
            '⏸️ Reset bloqueado por cooldown. El usuario debe recargar manualmente.'
          );
          // status se queda en 'failed' para que la UI muestre un botón manual
        }
        return;
      }

      // ── Retry normal con backoff ───────────────────────────────────────
      const retryDelay = this.installFailureCount * 20000; // 20s, 40s...
      this.log(
        'info',
        `🔄 Reintento ${this.installFailureCount} en ${retryDelay / 1000}s...`
      );

      this.retrySubscription = timer(retryDelay)
        .pipe(
          filter(() => this.enabled),
          tap(() => {
            this.log(
              'info',
              `🔄 RETRY ${this.installFailureCount}: checkForUpdate()`
            );
            this.status.set('checking');
          }),
          switchMap(() => from(this.swUpdate.checkForUpdate())),
          catchError(err => {
            this.log('error', '❌ Retry fallido', err);
            this.status.set('failed');
            return of(false);
          })
        )
        .subscribe();
    });

    // Effect 3: UNRECOVERABLE → nuclear reset directo (sin cooldown)
    effect(() => {
      const event = this.unrecoverable();
      if (!event) return;
      this.log('error', `🚨 UNRECOVERABLE: ${event.reason}`, event);
      this.status.set('resetting');
      this.notifyUser('resetting');
      timer(4000)
        .pipe(switchMap(() => from(this.performNuclearReset())))
        .subscribe();
    });
  }

  // ─── Nuclear reset ────────────────────────────────────────────────────────

  /**
   * Desregistra todos los Service Workers, limpia todas las cachés del
   * navegador (Cache Storage API) y recarga la página desde la red.
   *
   * Tras la recarga el SW se registra desde cero como en la primera visita.
   * Guarda el timestamp en sessionStorage para activar el cooldown.
   */
  private async performNuclearReset(): Promise<void> {
    this.log('warn', '💣 NUCLEAR RESET: Limpieza completa del SW y cachés...');

    try {
      // 1. Marcar el momento del reset antes de recargar
      sessionStorage.setItem(SW_RESET_KEY, Date.now().toString());

      // 2. Desregistrar todos los Service Workers del scope
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(r => {
            this.log('info', `  → Desregistrando: ${r.scope}`);
            return r.unregister();
          })
        );
        this.log('info', `✅ ${registrations.length} SW desregistrado(s)`);
      }

      // 3. Limpiar TODAS las cachés del Cache Storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => {
            this.log('info', `  → Eliminando caché: ${name}`);
            return caches.delete(name);
          })
        );
        this.log('info', `✅ ${cacheNames.length} caché(s) eliminada(s)`);
      }

      this.log('warn', '🔄 Limpieza completa. Recargando desde red...');

      // 4. Recargar forzando descarga desde red
      window.location.reload();
    } catch (err) {
      this.log(
        'error',
        '❌ Error durante nuclear reset — recargando de todas formas',
        err
      );
      window.location.reload();
    }
  }

  /**
   * Verifica si está permitido un reset automático.
   * Bloquea si ya se hizo uno en las últimas SW_RESET_COOLDOWN_MS.
   * El cooldown evita loops: reset → recarga → SW sigue roto → reset → ...
   */
  private canPerformReset(): boolean {
    try {
      const lastReset = sessionStorage.getItem(SW_RESET_KEY);
      if (!lastReset) return true;

      const elapsed = Date.now() - parseInt(lastReset, 10);
      if (elapsed < SW_RESET_COOLDOWN_MS) {
        this.log(
          'warn',
          `⏸️ Cooldown activo — último reset hace ${Math.round(elapsed / 60000)} min (cooldown: ${SW_RESET_COOLDOWN_MS / 60000} min)`
        );
        return false;
      }

      return true;
    } catch {
      return true; // sessionStorage no disponible → permitir reset
    }
  }

  /**
   * Al inicializar, loggea si la sesión actual es producto de un reset previo.
   * Útil para saber en los logs que el reset ocurrió y cuánto hace.
   */
  private logResetHistory(): void {
    try {
      const lastReset = sessionStorage.getItem(SW_RESET_KEY);
      if (lastReset) {
        const elapsed = Math.round(
          (Date.now() - parseInt(lastReset, 10)) / 1000
        );
        this.log(
          'info',
          `ℹ️ Esta sesión es post-nuclear-reset (hace ${elapsed}s)`
        );
      }
    } catch {
      /* sessionStorage no disponible */
    }
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  /**
   * Activa la versión del SW ya instalada y recarga la página.
   * Llamar desde un botón de UI cuando status === 'available'.
   */
  async activateUpdate(): Promise<void> {
    if (!this.updateAvailable()) {
      this.log('warn', '⚠️ activateUpdate() llamado sin update disponible');
      return;
    }

    this.log('info', '🚀 Activando actualización...');
    this.status.set('activating');
    this.notifyUser('activating');

    try {
      await this.swUpdate.activateUpdate();
      this.log('info', '✅ Actualización activada. Recargando...');
      window.location.reload();
    } catch (err) {
      this.log('error', '❌ Error al activar. Recargando...', err);
      this.status.set('failed');
      this.notifyUser('failed');
      window.location.reload();
    }
  }

  /**
   * Nuclear reset manual — para exponer en un botón de UI cuando
   * status === 'failed' y el cooldown automático está activo.
   * Ignora el cooldown porque el usuario lo está pidiendo explícitamente.
   */
  async forceReset(): Promise<void> {
    this.log('warn', '🔧 Nuclear reset manual solicitado por el usuario');
    try {
      sessionStorage.removeItem(SW_RESET_KEY);
    } catch {
      /* ok */
    }
    this.status.set('resetting');
    this.notifyUser('resetting');
    await this.performNuclearReset();
  }

  // ─── Notificación ─────────────────────────────────────────────────────────
  private notifyUser(status: UpdateStatus): void {
    const messages: Record<UpdateStatus, string> = {
      idle: '[SW] La app está al día.',
      checking: '[SW] Buscando actualizaciones...',
      available: '[SW] Nueva versión disponible.',
      activating: '[SW] Instalando nueva versión...',
      failed: '[SW] No se pudo instalar la actualización.',
      unrecoverable: '[SW] Estado irrecuperable. Limpiando...',
      resetting: '[SW] Limpieza completa en curso. La app se recargará...',
    };
    const msg = messages[status] ?? `[SW] Estado: ${status}`;
    console.log(msg);
    this.log('info', msg);
  }
}
